import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../../app";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { DefaultEventService } from "../../../services/event-service";
import { KeyboardMappingService } from "../../../services/keyboardmapping-service";
import { SaveGameService } from "../../../services/save-game-service";
import {
    ChoiceDialogContentDTO,
    ChoiceDTO,
    GameStateDTO,
    NewGameError,
} from "../../../shared/dtos";
import { Direction, SocketEvents } from "../../../shared/enum";
import { DefaultHorizontalMenu } from "../../components/base-menu";
import { LoadingGameCard } from "../../components/title-screen/loading-game-card";
import { ThemeManager } from "../../design-system/theme-manager";
import { ConfirmationDialog } from "../../menu/dialogs/confirmation-dialog";
import { AbstractMapScene } from "../../scenes/maps/abstract-map-scene";
import { StyleOptions } from "../interfaces";

const { lazyInject } = getDecorators(container);

export class CreateGameMenu extends DefaultHorizontalMenu<LoadingGameCard> {
    @lazyInject(TYPES.KeyboardMappingService)
    private keyboardMappingService: KeyboardMappingService;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    @lazyInject(TYPES.ThemeManager)
    private themeManager: ThemeManager;

    @lazyInject(TYPES.SaveGameService)
    private saveGameService: SaveGameService;

    private currentPage: number = 0;
    private slotsPerPage: number;
    private allSlots: Array<GameStateDTO | null>;
    private totalPages: number;
    private onPageChangeCallback: (page: number, totalPages: number) => void;

    // Dialog state for delete confirmation
    private deleteConfirmationDialog: ConfirmationDialog | null = null;
    private pendingDeleteIndex: number | null = null;

    // Screen margin for viewport calculation
    private static readonly SCREEN_MARGIN = 40;

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        loadGames: Array<GameStateDTO>,
        scene: Phaser.Scene
    ) {
        super(scene, x, y);
        this.scene = scene;

        // Calculate how many slots can fit on screen
        this.calculateSlotsPerPage();

        // Store all slots (including empty ones to reach max of 3)
        this.allSlots = [...loadGames];
        if (this.allSlots.length < 3) {
            for (let i = this.allSlots.length; i < 3; i++) {
                this.allSlots.push(null);
            }
        }

        // Calculate total pages
        this.calculatePagination();

        // Render first page
        this.renderCurrentPage();

        // Register new game event handlers (matches backend implementation)
        this.eventService.register(SocketEvents.NEW_GAME_STARTED, (gameState: GameStateDTO) =>
            this.onGameStarted(gameState)
        );
        this.eventService.register(SocketEvents.NEW_GAME_ERROR, (error: NewGameError) =>
            this.onNewGameError(error)
        );
    }

    private calculateSlotsPerPage() {
        const spacing = this.themeManager.getSpacing("xl");
        const availableWidth = this.scene.cameras.main.width - CreateGameMenu.SCREEN_MARGIN * 2;
        const slotWidth = LoadingGameCard.CARD_WIDTH + spacing;
        const maxVisibleSlots = Math.floor(availableWidth / slotWidth);

        // Ensure at least 1 slot visible
        this.slotsPerPage = Math.max(1, maxVisibleSlots);
    }

    private calculatePagination() {
        this.totalPages = Math.ceil(this.allSlots.length / this.slotsPerPage);
        // Clamp current page
        this.currentPage = Math.max(0, Math.min(this.currentPage, this.totalPages - 1));
    }

    private getVisibleSlots(): Array<GameStateDTO | null> {
        const startIndex = this.currentPage * this.slotsPerPage;
        const endIndex = startIndex + this.slotsPerPage;
        return this.allSlots.slice(startIndex, endIndex);
    }

    private renderCurrentPage() {
        // Clear existing items
        this.clearMenuItems();

        // Add visible slots for current page
        const visibleSlots = this.getVisibleSlots();
        visibleSlots.forEach((loadGame: GameStateDTO | null) => {
            this.addMenuItem(
                new LoadingGameCard(
                    LoadingGameCard.CARD_WIDTH,
                    LoadingGameCard.CARD_HEIGHT,
                    loadGame,
                    this.scene
                )
            );
        });

        // Layout items
        const spacing = this.themeManager.getSpacing("xl");
        this.centerElementsX({ spaceBetween: spacing } as StyleOptions);
        this.centerElementsY();

        // Select first item
        if (this.menuItems.length > 0) {
            this.menuItems[0].select();
        }

        // Notify parent of page change
        if (this.onPageChangeCallback) {
            this.onPageChangeCallback(this.currentPage, this.totalPages);
        }
    }

    private clearMenuItems() {
        // Destroy existing menu items
        this.menuItems.forEach((item) => {
            this.remove(item);
            item.destroy();
        });
        this.menuItems.length = 0;
        this._menuItemIndex = 0;
    }

    public nextPage() {
        if (this.currentPage >= this.totalPages - 1) return;

        this.currentPage++;
        this.refreshPage();
    }

    public previousPage() {
        if (this.currentPage <= 0) return;

        this.currentPage--;
        this.refreshPage();
    }

    private refreshPage() {
        this.renderCurrentPage();

        // Optional: Add fade transition
        this.alpha = 0;
        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            duration: 200,
        });
    }

    public setPageChangeCallback(callback: (page: number, totalPages: number) => void) {
        this.onPageChangeCallback = callback;
        // Trigger initial callback
        if (callback) {
            callback(this.currentPage, this.totalPages);
        }
    }

    public canGoToPreviousPage(): boolean {
        return this.currentPage > 0;
    }

    public canGoToNextPage(): boolean {
        return this.currentPage < this.totalPages - 1;
    }

    public getCurrentPage(): number {
        return this.currentPage;
    }

    public getTotalPages(): number {
        return this.totalPages;
    }

    // Override keyboard input to handle page navigation and delete
    public onKeyInput(event: KeyboardEvent) {
        // If dialog is open, handle dialog navigation only
        if (this.deleteConfirmationDialog) {
            if (event.code === "ArrowLeft") {
                this.deleteConfirmationDialog.changeSelection(Direction.LEFT);
            } else if (event.code === "ArrowRight") {
                this.deleteConfirmationDialog.changeSelection(Direction.RIGHT);
            } else if (event.code === "Space") {
                this.deleteConfirmationDialog.onAction();
            }
            return;
        }

        // Normal menu navigation
        if (event.code === "ArrowRight") {
            // If at rightmost item, try to go to next page
            if (this._menuItemIndex >= this.menuItems.length - 1 && this.canGoToNextPage()) {
                this.nextPage();
            } else {
                this.moveSelectionRight();
            }
        } else if (event.code === "ArrowLeft") {
            // If at leftmost item, try to go to previous page
            if (this._menuItemIndex <= 0 && this.canGoToPreviousPage()) {
                this.previousPage();
            } else {
                this.moveSelectionLeft();
            }
        } else if (event.code === "Space") {
            this.confirm();
        } else if (event.code === "Delete") {
            this.requestDeleteCurrentSave();
        }
    }

    public draw() {
        const graphics = this.scene.add.graphics();
        //@ts-ignore
        const menu = this.scene.add.container();
        this.menuItems.forEach((item) => menu.add(item));
    }

    private requestDeleteCurrentSave() {
        // Calculate the actual slot index across all pages
        const actualIndex = this.currentPage * this.slotsPerPage + this.menuItemIndex;
        const loadGame = this.allSlots[actualIndex];

        // Can't delete empty slots
        if (!loadGame) {
            return;
        }

        // Store pending delete index
        this.pendingDeleteIndex = actualIndex;

        // Show confirmation dialog
        const dialogContent = new ChoiceDialogContentDTO();
        dialogContent.message = `Delete "${loadGame.save_name}"?`;
        dialogContent.choices = [
            Object.assign(new ChoiceDTO(), { choice: "Yes", next: [] }),
            Object.assign(new ChoiceDTO(), { choice: "No", next: [] }),
        ];
        dialogContent.wait_for_player_input = true;

        this.deleteConfirmationDialog = new ConfirmationDialog(this.scene, dialogContent);
        this.deleteConfirmationDialog.draw();
        this.deleteConfirmationDialog.setText(dialogContent.message);

        // Listen for dialog choice on the scene's event system
        this.scene.events.once("dialog_choice", (selection: number) => {
            this.onDeleteConfirmation(selection);
        });
    }

    private async onDeleteConfirmation(selection: number) {
        // Clean up dialog reference (scene will clean up the container)
        if (this.deleteConfirmationDialog) {
            this.deleteConfirmationDialog = null;
        }

        // If user selected "No" (index 1), do nothing
        if (selection !== 0) {
            this.pendingDeleteIndex = null;
            return;
        }

        // User selected "Yes", proceed with deletion
        if (this.pendingDeleteIndex === null) {
            return;
        }

        const loadGame = this.allSlots[this.pendingDeleteIndex];
        if (!loadGame) {
            this.pendingDeleteIndex = null;
            return;
        }

        try {
            // Delete save via backend
            await this.saveGameService.deleteSave(loadGame.save_id);

            // Remove from array
            this.allSlots.splice(this.pendingDeleteIndex, 1);

            // Ensure at least 3 slots (add null for empty slot)
            if (this.allSlots.length < 3) {
                this.allSlots.push(null);
            }

            // Recalculate pagination
            this.calculatePagination();

            // If current page is now empty and not the first page, go to previous page
            if (this.currentPage > 0 && this.currentPage >= this.totalPages) {
                this.currentPage = this.totalPages - 1;
            }

            // Refresh the page
            this.refreshPage();
        } catch (error) {
            console.error("Failed to delete save:", error);
            this.showError("Delete Failed", (error as Error).message);
        } finally {
            this.pendingDeleteIndex = null;
        }
    }

    public destroy() {
        // Clean up dialog reference if still open (scene will clean up the container)
        if (this.deleteConfirmationDialog) {
            this.deleteConfirmationDialog = null;
        }

        // Remove any pending dialog choice listeners
        this.scene.events.off("dialog_choice");

        // Clean up event listeners to prevent memory leaks
        this.eventService.off(SocketEvents.NEW_GAME_STARTED);
        this.eventService.off(SocketEvents.NEW_GAME_ERROR);
        super.destroy();
    }

    public confirm() {
        // Calculate the actual slot index across all pages
        const actualIndex = this.currentPage * this.slotsPerPage + this.menuItemIndex;
        const loadGame = this.allSlots[actualIndex];

        if (loadGame) {
            console.log("confirming creategamemenu");
            // Existing save slot - show confirmation dialog for overwriting
            const dialogContent = new ChoiceDialogContentDTO();
            dialogContent.message = "Overwrite existing save?";
            dialogContent.choices = [
                Object.assign(new ChoiceDTO(), { choice: "Yes", next: [] }),
                Object.assign(new ChoiceDTO(), { choice: "No", next: [] }),
            ];
            dialogContent.wait_for_player_input = true;
            const dialog = new ConfirmationDialog(this.scene, dialogContent);
        } else {
            // Empty slot - create new game
            this.eventService.emit(SocketEvents.START_NEW_GAME, {
                project_id: SimpleGame.PROJECT_ID, // Using project_id (publication system not yet implemented)
                save_name: "New Game", // Default save name
                character_name: undefined, // Use default character name from project
            });
        }
    }

    private onGameStarted(gameState: GameStateDTO) {
        console.log("New game started successfully:", gameState);

        // Store save_id for future operations
        SimpleGame.CURRENT_GAME = gameState.save_id;

        // Add the scene to Phaser's scene manager first (if not already added)
        // Map scenes are dynamically created, not pre-registered in app.ts
        this.scene.scene.add(
            gameState.game_location.scene_id,
            new AbstractMapScene(gameState.game_location.scene_id),
            false,
            { sceneId: gameState.game_location.scene_id }
        );

        // Transition to the game scene at the starting location
        this.scene.scene.start(gameState.game_location.scene_id, {
            sceneId: gameState.game_location.scene_id,
        });
    }

    private onNewGameError(error: NewGameError) {
        console.error("Failed to start new game:", error);
        this.handleNewGameError(error);
    }

    private handleNewGameError(error: NewGameError) {
        let title = "Error";
        let message = `Failed to start new game: ${error.message}`;

        switch (error.code) {
            case "NO_STARTING_PARTY":
                title = "Configuration Error";
                message =
                    "This game has no starting party configured. Please contact the game developer.";
                break;

            case "MISSING_CHARACTER_TEMPLATE":
                title = "Configuration Error";
                message = `Missing character template: ${error.message}`;
                break;

            case "MISSING_ITEM_DEFINITION":
                title = "Configuration Error";
                message = `Missing item definition: ${error.message}`;
                break;

            case "PROJECT_NOT_PUBLISHED":
                title = "Not Published";
                message = "This game project has not been published yet.";
                break;

            case "PUBLICATION_NOT_FOUND":
                title = "Not Found";
                message = "Game publication not found.";
                break;

            case "DATABASE_ERROR":
                title = "Database Error";
                message = "A database error occurred. Please try again later.";
                break;
        }

        this.showError(title, message);
    }

    private showError(title: string, message: string) {
        // Simple error display - can be improved with a proper dialog component later
        const errorText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY,
            `${title}\n\n${message}\n\nClick to return to title screen.`,
            {
                fontSize: "18px",
                color: "#ff0000",
                fontFamily: "monospace",
                align: "center",
                backgroundColor: "#000000",
                padding: { x: 20, y: 20 },
            }
        );
        errorText.setOrigin(0.5);
        errorText.setInteractive();
        errorText.on("pointerdown", () => {
            this.scene.scene.start("TitleScreen");
        });
    }
}
